/**
 * The local library, over Dexie.
 *
 * The port is in `../ports`; this is the browser's implementation of it, and
 * `sqlite/catalog.ts` is the phone's. Both answer to `__tests__/catalog-contract.ts`, so two
 * stores cannot drift into two ideas of what creating a map means.
 *
 * Creating a map writes its listing row *and* its first checkpoint, in one transaction. That is
 * what makes every map openable without a caller inventing a base document to replay over — and
 * a map row with no checkpoint would be a map that appears in the library and cannot be opened,
 * which is worse than one that was never created.
 *
 * Trash is a column, not a table. A second table would need every query to remember it exists,
 * and the first one to forget would be the one that permanently deleted something.
 */
import { nodeCount as countNodes } from '@orbit-mind/domain';
import { err, ok, } from '@orbit-mind/shared-types';
import { StorageError, storageFailure } from '../errors';
import { createDatabase } from './database';
import { readCheckpointRow, readMapRow, toCheckpointRow, toMapRow } from './rows';
export function createLocalMapCatalog(db = createDatabase()) {
    async function guarded(operation, body) {
        try {
            return ok(await body());
        }
        catch (cause) {
            return err(storageFailure(operation, cause));
        }
    }
    /**
     * Rewrites the stored map with a new `deletedAt`.
     *
     * Both the indexed column and the map's own field, because they are the same fact: the column
     * is what the trash query reads, the field is what the editor and every export read, and one
     * of them being stale is how a map comes back from the trash still believing it is deleted.
     */
    async function setDeletedAt(mapId, deletedAt, remote = false) {
        await db.transaction('rw', db.maps, db.checkpoints, db.lifecycle, async () => {
            if (remote && (await db.lifecycle.get(mapId))?.pending === 1)
                return;
            const row = await db.maps.get(mapId);
            if (row === undefined) {
                throw new StorageError('MAP_NOT_FOUND', 'no such map in the local library', {
                    details: { mapId },
                    retryable: false,
                });
            }
            const read = readMapRow(row);
            if (!read.ok) {
                throw new StorageError('MAP_CORRUPTED', 'the map row could not be read back', {
                    details: { mapId, reason: read.reason },
                    retryable: false,
                });
            }
            await db.maps.put({ ...row, deletedAt, map: { ...read.summary.map, deletedAt } });
            const checkpoint = await db.checkpoints.get(mapId);
            if (checkpoint !== undefined) {
                const parsed = readCheckpointRow(checkpoint);
                if (!parsed.ok)
                    throw new StorageError('MAP_CORRUPTED', parsed.reason, { retryable: false });
                const document = parsed.record.document;
                await db.checkpoints.put({
                    ...checkpoint,
                    document: { ...document, map: { ...document.map, deletedAt } },
                });
            }
            if (!remote) {
                const previous = await db.lifecycle.get(mapId);
                await db.lifecycle.put({
                    mapId,
                    deletedAt,
                    version: (previous?.version ?? 0) + 1,
                    pending: 1,
                });
            }
        });
    }
    return {
        replace(document, at, requireClean = true) {
            return guarded('replace map', async () => {
                await db.transaction('rw', db.maps, db.journal, db.checkpoints, db.lifecycle, async () => {
                    if (requireClean && (await db.lifecycle.get(document.map.id))?.pending === 1) {
                        throw new StorageError('REVISION_CONFLICT', 'A local trash change is still pending.', {
                            retryable: false,
                        });
                    }
                    if (requireClean &&
                        (await db.journal.where('[mapId+synced]').equals([document.map.id, 0]).count()) > 0) {
                        throw new StorageError('REVISION_CONFLICT', 'There are unsent edits. Keep both versions before replacing this map.', { retryable: false });
                    }
                    await db.journal.where('mapId').equals(document.map.id).delete();
                    await db.checkpoints.put(toCheckpointRow({ mapId: document.map.id, sequence: 0, document, createdAt: at }));
                    await db.maps.put(toMapRow(document, countNodes(document)));
                });
            });
        },
        pendingTrash: () => guarded('read pending trash', async () => db.lifecycle.where('pending').equals(1).toArray()),
        acknowledgeTrash: (change) => guarded('acknowledge trash', async () => {
            await db.transaction('rw', db.lifecycle, async () => {
                const row = await db.lifecycle.get(change.mapId);
                if (row?.version === change.version)
                    await db.lifecycle.put({ ...row, pending: 0 });
            });
        }),
        applyRemoteTrash: (mapId, deletedAt) => guarded('apply remote trash', () => setDeletedAt(mapId, deletedAt, true)),
        create(document, at) {
            return guarded('create map', async () => {
                await db.transaction('rw', db.maps, db.checkpoints, async () => {
                    // `add`, not `put`: creating over an existing map would replace its listing row while
                    // its journal carried on from wherever it was, and the two would describe different
                    // documents from then on.
                    await db.maps.add(toMapRow(document, countNodes(document)));
                    await db.checkpoints.put(toCheckpointRow({ mapId: document.map.id, sequence: 0, document, createdAt: at }));
                });
            });
        },
        save(document) {
            return guarded('save map', async () => db.maps.put(toMapRow(document, countNodes(document)))).then((result) => (result.ok ? ok(undefined) : result));
        },
        list(options) {
            return guarded('list maps', async () => {
                const rows = await db.maps.orderBy('updatedAt').reverse().toArray();
                const maps = [];
                const damaged = [];
                for (const row of rows) {
                    const read = readMapRow(row);
                    if (!read.ok) {
                        // No sequence to name — a map row is not a journal entry — so the reason carries the
                        // whole story.
                        damaged.push({ sequence: null, reason: read.reason });
                        continue;
                    }
                    if (read.summary.map.deletedAt !== null && options?.includeTrashed !== true)
                        continue;
                    maps.push(read.summary);
                }
                return { maps, damaged };
            });
        },
        get(mapId) {
            return guarded('get map', async () => {
                const row = await db.maps.get(mapId);
                if (row === undefined)
                    return undefined;
                const read = readMapRow(row);
                if (read.ok)
                    return read.summary;
                throw new StorageError('MAP_CORRUPTED', 'the map row could not be read back', {
                    details: { mapId, reason: read.reason },
                    retryable: false,
                });
            });
        },
        moveToTrash(mapId, at) {
            return guarded('trash map', () => setDeletedAt(mapId, at));
        },
        restoreFromTrash(mapId) {
            return guarded('restore map', () => setDeletedAt(mapId, null));
        },
        purge(mapId) {
            return guarded('purge map', async () => {
                await db.transaction('rw', [
                    db.maps,
                    db.journal,
                    db.checkpoints,
                    db.assets,
                    db.attachments,
                    db.lifecycle,
                    db.settings,
                ], async () => {
                    await db.maps.delete(mapId);
                    await db.journal.where('mapId').equals(mapId).delete();
                    await db.checkpoints.delete(mapId);
                    await db.assets.where('mapId').equals(mapId).delete();
                    await db.attachments.where('mapId').equals(mapId).delete();
                    await db.settings.delete(`text-draft:${mapId}`);
                    // Retain an unsent deletion even after the local bytes have been removed.
                });
            });
        },
    };
}
//# sourceMappingURL=catalog.js.map